import React, { createContext, useContext, useEffect, useState } from "react";
import config from "../../../../config/config";
import { AdSenseConfig, AdSenseContextType } from "../../types";

const AdSenseContext = createContext<AdSenseContextType | undefined>(undefined);

export const AdSenseProvider: React.FC<{ children: React.ReactNode; allowAds?: boolean }> = ({
  children,
  allowAds = false,
}) => {
  const [isAdSenseLoaded, setIsAdSenseLoaded] = useState(false);
  const adConfig: AdSenseConfig = config.ads.adsense;

  // Suppress AdSense-related console errors (403s, etc.) to prevent confusion
  // This runs once on mount and helps prevent ad errors from polluting console
  useEffect(() => {
    // Keep the previous behavior (silence some AdSense noise) but avoid
    // doing heavy string joins on every console call. Only filter when the
    // message obviously references AdSense domains.
    const originalConsoleError = console.error;
    console.error = (...args: any[]) => {
      try {
        const first = args[0] || '';
        const msg = typeof first === 'string' ? first : JSON.stringify(first);
        if (
          msg.includes('adsbygoogle') ||
          msg.includes('pagead2.googlesyndication.com') ||
          msg.includes('ads?client=ca-pub') ||
          (msg.includes('Failed to load resource') && msg.includes('403'))
        ) {
          // Silently ignore AdSense-related errors
          return;
        }
      } catch (e) {
        // fall back to normal behavior
      }
      originalConsoleError.apply(console, args);
    };

    return () => {
      console.error = originalConsoleError;
    };
  }, []);

  // Load AdSense script function
  const loadAdSenseScript = React.useCallback(async (): Promise<void> => {
    return new Promise((resolve, reject) => {
      // Check if AdSense script is already loaded
      if (window.adsbygoogle) {
        setIsAdSenseLoaded(true);
        resolve();
        return;
      }

      // Check if script tag already exists
      const existingScript = document.querySelector(
        `script[src*="adsbygoogle.js"]`
      );
      if (existingScript) {
        existingScript.addEventListener("load", () => {
          setIsAdSenseLoaded(true);
          resolve();
        });
        return;
      }

      // Create and load the AdSense script
      const script = document.createElement("script");
      script.async = true;
      script.crossOrigin = "anonymous";
      script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${adConfig.adClient}`;

      script.onload = () => {
        // Initialize adsbygoogle array if it doesn't exist
        window.adsbygoogle = window.adsbygoogle || [];
        setIsAdSenseLoaded(true);
        resolve();
      };
      // Inform developers that AdSense scripts have loaded
      console.info('AD: AdSense script loaded and initialized');


      script.onerror = (error) => {
        console.error("❌ Failed to load AdSense script:", error);
        // Mark as loaded even on error to prevent retries that could block rendering
        setIsAdSenseLoaded(true);
        // Still resolve to prevent blocking the app
        resolve();
      };

      document.head.appendChild(script);
    });
  }, [adConfig]);

  // Load AdSense script when component mounts if enabled
  useEffect(() => {
    // Defer loading the AdSense script to avoid interfering with first paint
    // Delay significantly to avoid blocking critical rendering and layout
    const refs = {
      timeoutId: 0 as number | undefined,
      idleCallbackId: 0 as number | undefined,
    };

    const scheduleLoad = () => {
      if (!adConfig.enabled || isAdSenseLoaded) return;

      // Use a longer delay (3 seconds) to let the page fully settle
      // This prevents AdSense from interfering with initial rendering and navigation
      refs.timeoutId = window.setTimeout(() => {
        // Only load if the page has been idle for a bit
        if ('requestIdleCallback' in window) {
          refs.idleCallbackId = (window as any).requestIdleCallback(
            () => {
              loadAdSenseScript().catch((error) => {
                console.error('💥 loadAdSenseScript failed:', error);
              });
            },
            { timeout: 3000 }
          );
        } else {
          loadAdSenseScript().catch((error) => {
            console.error('💥 loadAdSenseScript failed:', error);
          });
        }
      }, 3000);
    };

    // Only schedule loading when ads are allowed for this user and AdSense is enabled
    if (allowAds && adConfig.enabled && !isAdSenseLoaded) {
      // Defer loading the AdSense script to avoid blocking initial render
      scheduleLoad();
    }

    // If ads became disallowed while AdSense is loaded, remove script and any inserted
    // ad placeholders to respect the user's subscription permissions.
    const removeAdSenseElements = () => {
      try {
        // Remove adsbygoogle script tag(s)
        const scripts = Array.from(document.querySelectorAll('script[src*="adsbygoogle.js"]'));
        scripts.forEach((s) => s.parentNode?.removeChild(s));

        // Remove injected ins.adsbygoogle elements
        const ins = Array.from(document.querySelectorAll('ins.adsbygoogle'));
        ins.forEach((e) => e.parentNode?.removeChild(e));

        // Remove global var to prevent further operations
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        if (window.adsbygoogle) {
          try {
            // Reset it to undefined
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            window.adsbygoogle = undefined;
          } catch (e) {
            // ignore
          }
        }

        setIsAdSenseLoaded(false);
      } catch (e) {
        // ignore cleanup errors
        console.info('AD: AdSense disabled and cleanup executed (ads removed)');
      }
    };

    if (!allowAds && isAdSenseLoaded) {
      removeAdSenseElements();
      console.info('AD: AdSense disabled and cleanup executed (ads removed)');
    }

    return () => {
      // Cleanup any scheduled callbacks when unmounting
      try {
        if (refs.timeoutId) clearTimeout(refs.timeoutId);
        if (refs.idleCallbackId && 'cancelIdleCallback' in window) {
          (window as any).cancelIdleCallback(refs.idleCallbackId);
        }
      } catch (e) {
        // ignore
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowAds, adConfig.enabled, isAdSenseLoaded]);

  const value: AdSenseContextType = {
    config: adConfig,
    isAdSenseLoaded,
    loadAdSenseScript,
  };

  return (
    <AdSenseContext.Provider value={value}>{children}</AdSenseContext.Provider>
  );
};

export const useAdSense = (): AdSenseContextType => {
  const context = useContext(AdSenseContext);
  if (!context) {
    throw new Error("useAdSense must be used within an AdSenseProvider");
  }
  return context;
};
